import { DomElementEvent } from '../dom/element.event';
import { BrowserWindowEvent } from './window.event';
import { WindowServiceInterface } from '../../service/window/service.interface';
import { EventEmitterInterface } from '../event/emitter.interface';
import { BrowserViewportWatcherInterface } from './viewport-watcher.interface';
import { EventEmitterAwareInterface } from '../event/emitter-aware.interface';
import { BrowserViewportOptionsInterface } from './viewport-options.interface';

export class BrowserViewport implements EventEmitterAwareInterface {
  public static STATE_IN: string = 'in';
  public static STATE_OUT: string = 'out';
  public static DEBOUNCE_TIME: number = 200; // ms

  /**
   * Debounce timer
   */
  protected debounceTimer: number = 0;

  /**
   * List of registered watchers
   */
  protected watcher: { [key: string]: BrowserViewportWatcherInterface } = {};

  /**
   *
   * @param {EventEmitterInterface} eventEmitter
   * @param {WindowServiceInterface} windowService
   */
  constructor(private eventEmitter: EventEmitterInterface, private windowService: WindowServiceInterface) {
    this.observers();
  }

  /**
   * @inheritDoc
   */
  public getEventEmitter(): EventEmitterInterface {
    return this.eventEmitter;
  }

  /**
   * Return a browser viewport watcher
   *
   * @param id
   */
  public getWatcher(id: string): BrowserViewportWatcherInterface {
    return this.watcher[id];
  }

  /**
   * @inheritDoc
   */
  public registerWatcher(
    id: string,
    element: HTMLElement,
    options?: BrowserViewportOptionsInterface
  ): BrowserViewportWatcherInterface | null {
    // Store watcher
    this.watcher[id] = {
      id,
      element: element,
      state: '',
      options: options || null,
    };

    // Update watcher
    this.updateWatcher(id);
    return this.getWatcher(id);
  }

  /**
   * Unregister watcher
   */
  public unregisterWatcher(id: string): void {
    if (!this.watcher[id]) {
      return;
    }

    if (this.watcher[id].animationFrame) {
      this.windowService.getNative().cancelAnimationFrame(this.watcher[id].animationFrame);
    }

    delete this.watcher[id];
  }

  /**
   * @inheritDoc
   */
  public resetWatcher(id: string): void {
    if (!this.watcher[id]) {
      return;
    }

    // Reset state
    this.watcher[id].state = '';

    // Update watcher
    this.updateWatcher(id);
  }

  /**
   * Update registered watcher
   *
   * @param id
   */
  public updateWatcher(id: string): void {
    // No element registered for this watcher ID
    if (this.watcher[id] && this.watcher[id].animationFrame) {
      this.windowService.getNative().cancelAnimationFrame(this.watcher[id].animationFrame);
    }

    if (!this.watcher[id] || !this.watcher[id].element) {
      // Delete watcher
      delete this.watcher[id];

      return;
    }

    this.watcher[id].animationFrame = this.windowService.getNative().requestAnimationFrame(this.calculateState(id));
  }

  /**
   * Attach listeners
   */
  private observers(): void {
    this.windowService.getEventEmitter().addListener(BrowserWindowEvent.resize, this.onResizeWindow);

    this.windowService.getEventEmitter().addListener(BrowserWindowEvent.scroll, this.onScrollWindow);
  }

  /**
   * Update watchers
   */
  private update(): void {
    // Debouncing the call
    if (!this.debounceTimer) {
      const window = this.windowService.getNative();

      // Have to use "window" else TypeScript will be looking for nodeJS Timer
      this.debounceTimer = window.setTimeout(() => {
        this.debounceTimer = 0;

        // Update watchers
        Object.keys(this.watcher).forEach((id) => this.updateWatcher(id));
      }, BrowserViewport.DEBOUNCE_TIME);
    }
  }

  /**
   * Calculate watcher state
   */
  private calculateState = (id: string) => () => {
    // Window native object
    const window = this.windowService.getNative();

    // viewport dimensions
    let screenBottom = this.windowService.getInnerHeight() || window.document.documentElement.clientHeight;
    let screenTop = 0;

    // Distance from the top of the viewport
    const rect = this.watcher[id].element.getBoundingClientRect();
    const top = rect.top;
    const bottom = rect.bottom;

    if (this.watcher[id].options && this.watcher[id].options.offset) {
      screenTop -= this.watcher[id].options.offset;
      screenBottom += this.watcher[id].options.offset;
    }

    let eventName = '';

    // Element was NOT IN and is now IN the viewport
    if (this.watcher[id].state !== BrowserViewport.STATE_IN && bottom > screenTop && top < screenBottom) {
      eventName = DomElementEvent.enterViewport;

      // Update state
      this.watcher[id].state = BrowserViewport.STATE_IN;

      // Element was IN, and is now OUT of the viewport
    } else if (this.watcher[id].state === BrowserViewport.STATE_IN && (bottom <= screenTop || top >= screenBottom)) {
      eventName = DomElementEvent.leaveViewport;

      // Update state
      this.watcher[id].state = BrowserViewport.STATE_OUT;
    }

    if (eventName) {
      // Emit event
      this.getEventEmitter().emit(eventName, id);
    }
  };

  /**
   * Resized viewport
   */
  private onResizeWindow = (): void => {
    this.update();
  };

  /**
   * Scrolled viewport
   */
  private onScrollWindow = (): void => {
    this.update();
  };
}
